import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown, Clock, X, Plus, Inbox, AlertTriangle } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths, subDays, startOfDay, endOfDay, parseISO } from 'date-fns';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import PayrollResourceSummary from '../components/payroll/PayrollResourceSummary';
import PayrollEntriesPanel from '../components/payroll/PayrollEntriesPanel';
import PayrollPastDuePanel from '../components/payroll/PayrollPastDuePanel';
import PayrollEditModal from '../components/payroll/PayrollEditModal';
import PayrollRequestsSection from '../components/payroll/PayrollRequestsSection';
import { PAYROLL_PERIOD_SETTINGS_CHANGED } from '../components/payroll/PayrollSettingsModal';
import RunPayrollModal from '../components/payroll/RunPayrollModal';
import PayrollHistoryModal from '../components/payroll/PayrollHistoryModal';
import { getPayrollEntries, updatePayrollEntry, deletePayrollEntry, createPayrollEntry, createCustomBonus, payrollPeriodBounds, type PayrollEntry } from '../lib/payroll';
import { getPayrollResourceTotals, confirmTimeclockEntry, type PayrollResourceTotals, type UpdateEntryResult } from '../lib/timeclock';
import { 
  getPayrollPeriodSettings, 
  getCurrentPeriodRange, 
  getLastPeriodRange,
  type PayrollPeriodSettings 
} from '../lib/payrollPeriodSettings';
import { getPendingPayrollRequestsCount } from '../lib/payrollRequests';
import { usePastDuePayrollPeriods } from '../hooks/usePastDuePayrollPeriods';
import type { PastDuePayrollPeriod } from '../lib/payrollPastDuePeriods';
import toast from 'react-hot-toast';


export default function ManagePayroll() {
  const { selectedBusiness } = useBusiness();
  const [entries, setEntries] = useState<PayrollEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  // Per-resource totals summed by the database; the source of truth for money on this page.
  const [dbTotals, setDbTotals] = useState<Record<string, PayrollResourceTotals>>({});
  const [settingsReady, setSettingsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [expandedResources, setExpandedResources] = useState<Set<string>>(new Set());
  const [expandedBookings, setExpandedBookings] = useState<Set<string>>(new Set());
  const [isPayrollRequestsModalOpen, setIsPayrollRequestsModalOpen] = useState(false);
  const [payrollSettings, setPayrollSettings] = useState<PayrollPeriodSettings | null>(null);
  const [isRunPayrollModalOpen, setIsRunPayrollModalOpen] = useState(false);
  const [runPayrollInitialPeriod, setRunPayrollInitialPeriod] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pastDueRefreshKey, setPastDueRefreshKey] = useState(0);

  const { periods: pastDuePeriods, hasLoaded: pastDueHasLoaded } = usePastDuePayrollPeriods(
    selectedBusiness?.id,
    payrollSettings,
    pastDueRefreshKey
  );

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PayrollEntry | null>(null);
  const [creatingResourceId, setCreatingResourceId] = useState<string | null>(null);
  const [creatingResourceName, setCreatingResourceName] = useState<string>('');
  const [createKind, setCreateKind] = useState<'timeclock' | 'bonus'>('timeclock');
  const [pickerPurpose, setPickerPurpose] = useState<'timeclock' | 'bonus'>('timeclock');

  // Employee picker for adding an entry from the page level (works even when
  // no resource has entries in the selected period)
  const [isResourcePickerOpen, setIsResourcePickerOpen] = useState(false);
  const [pickerResources, setPickerResources] = useState<Array<{
    id: string;
    first_name: string;
    last_name: string;
    avatar_url: string | null;
    compensation_type: string | null;
  }>>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  
  // Animation state for payroll requests modal
  const [isPayrollRequestsAnimating, setIsPayrollRequestsAnimating] = useState(false);

  // Payroll filter state (for Time Requests modal header)
  const [isPayrollFilterOpen, setIsPayrollFilterOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'pending' | 'approved' | 'denied'>('pending');

  // Dynamic DATE_RANGES based on payroll settings
  const DATE_RANGES = useMemo(() => {
    const standardRanges = [
  { label: 'Today', value: 'today', getRange: () => {
    const today = new Date();
    return { start: startOfDay(today), end: endOfDay(today) };
  }},
  { label: 'Yesterday', value: 'yesterday', getRange: () => {
    const yesterday = subDays(new Date(), 1);
    return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
  }},
  { label: 'Last 30 days', value: 'last_30_days', getRange: () => ({
    start: subDays(new Date(), 29),
    end: new Date()
  })},
  { label: 'This Month', value: 'this_month', getRange: () => ({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  })},
  { label: 'Last Month', value: 'last_month', getRange: () => {
    const lastMonth = subMonths(new Date(), 1);
    return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      }},
  { label: 'Custom Range', value: 'custom', getRange: () => ({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  })},
    ];

    // Add period-based ranges if settings exist
    if (payrollSettings) {
      return [
        { 
          label: 'Current Period', 
          value: 'current_period', 
          getRange: () => getCurrentPeriodRange(payrollSettings)
        },
        { 
          label: 'Last Period', 
          value: 'last_period', 
          getRange: () => getLastPeriodRange(payrollSettings)
        },
        ...standardRanges
      ];
    }

    return standardRanges;
  }, [payrollSettings]);

  // Selected range and date range states (must come after DATE_RANGES)
  const [selectedRange, setSelectedRange] = useState(
    DATE_RANGES.find(r => r.value === (payrollSettings ? 'current_period' : 'this_month'))!
  );
  const dateRange = useMemo(() => {
    if (selectedRange.value === 'custom') {
      if (!customStartDate || !customEndDate) {
        return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
      }
      const start = startOfDay(parseISO(customStartDate));
      const end = endOfDay(parseISO(customEndDate));
      if (start > end) {
        return { start: endOfDay(parseISO(customEndDate)), end: startOfDay(parseISO(customStartDate)) };
      }
      return { start, end };
    }
    return selectedRange.getRange();
  }, [selectedRange, customStartDate, customEndDate]);

  const dateRangeLabel = useMemo(() => {
    if (selectedRange.value === 'custom' && customStartDate && customEndDate) {
      const start = parseISO(customStartDate);
      const end = parseISO(customEndDate);
      return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
    }
    return selectedRange.label;
  }, [selectedRange, customStartDate, customEndDate]);

  const isCustomRangeValid =
    Boolean(customStartDate && customEndDate) &&
    parseISO(customStartDate) <= parseISO(customEndDate);

  // Load payroll settings, then set the correct period before fetching entries
  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      setSettingsReady(false);

      if (!selectedBusiness?.id) {
        setPayrollSettings(null);
        setSettingsReady(true);
        return;
      }

      try {
        const settings = await getPayrollPeriodSettings(selectedBusiness.id);
        if (cancelled) return;

        setPayrollSettings(settings);

        if (settings) {
          setSelectedRange({
            label: 'Current Period',
            value: 'current_period',
            getRange: () => getCurrentPeriodRange(settings),
          });
        }
      } catch (err) {
        console.error('Error loading payroll settings:', err);
      } finally {
        if (!cancelled) {
          setSettingsReady(true);
        }
      }
    };

    void loadSettings();

    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id]);

  // Load entries after settings are ready and whenever the period changes
  useEffect(() => {
    if (!settingsReady || !selectedBusiness?.id) return;
    void loadEntries();
  }, [dateRange, selectedBusiness?.id, settingsReady]);

  // Load pending requests count when business changes
  useEffect(() => {
    loadPendingRequestsCount();
  }, [selectedBusiness?.id]);

  // Set up realtime listener for payroll requests to update badge count
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    const channel = supabase
      .channel('payroll_requests_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_request',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        () => {
          // Reload the badge count whenever a request is added, updated, or deleted
          loadPendingRequestsCount();
        }
      )
      .subscribe();

    return () => {
      if (supabase) {
        supabase.removeChannel(channel);
      }
    };
  }, [selectedBusiness?.id]);

  const loadEntries = async () => {
    if (!selectedBusiness?.id) return;

    setEntriesLoading(true);
    setEntries([]);

    try {
      const startDate = format(dateRange.start, 'yyyy-MM-dd');
      const endDate = format(dateRange.end, 'yyyy-MM-dd');

      const { utcStart, utcEnd } = payrollPeriodBounds(startDate, endDate);
      const [data, totals] = await Promise.all([
        getPayrollEntries(selectedBusiness.id, startDate, endDate),
        getPayrollResourceTotals(selectedBusiness.id, utcStart, utcEnd),
      ]);
      setEntries(data);
      setDbTotals(totals);
      setError(null);
      setPastDueRefreshKey((key) => key + 1);
    } catch (err) {
      console.error('Error loading payroll entries:', err);
      setError('Failed to load payroll entries');
    } finally {
      setEntriesLoading(false);
    }
  };

  const loadPendingRequestsCount = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const count = await getPendingPayrollRequestsCount(selectedBusiness.id);
      setPendingRequestsCount(count);
    } catch (error) {
      console.error('Error loading pending requests count:', error);
      setPendingRequestsCount(0);
    }
  };

  // Animation handlers for payroll requests modal
  const handlePayrollRequestsTransitionEnd = () => {
    if (!isPayrollRequestsModalOpen) {
      setIsPayrollRequestsAnimating(false);
    }
  };

  const handleClosePayrollRequestsModal = () => {
    setIsPayrollRequestsModalOpen(false);
    setIsPayrollRequestsAnimating(true);
  };

  const handleOpenPayrollRequestsModal = () => {
    setIsPayrollRequestsModalOpen(true);
    setIsPayrollRequestsAnimating(false);
    // Refresh pending count when opening modal
    loadPendingRequestsCount();
  };

  useEffect(() => {
    const onSettingsChanged = (event: Event) => {
      const settings = (event as CustomEvent<PayrollPeriodSettings>).detail;
      if (!settings || settings.business_id !== selectedBusiness?.id) return;
      setPayrollSettings(settings);
      setPastDueRefreshKey((key) => key + 1);
      setSelectedRange((current) => {
        if (current.value !== 'current_period' && current.value !== 'last_period' && current.value !== 'this_month') {
          return current;
        }
        return {
          label: 'Current Period',
          value: 'current_period',
          getRange: () => getCurrentPeriodRange(settings),
        };
      });
    };
    window.addEventListener(PAYROLL_PERIOD_SETTINGS_CHANGED, onSettingsChanged);
    return () => window.removeEventListener(PAYROLL_PERIOD_SETTINGS_CHANGED, onSettingsChanged);
  }, [selectedBusiness?.id]);

  const handleSelectPastDuePeriod = (period: PastDuePayrollPeriod) => {
    setRunPayrollInitialPeriod({ start: period.start, end: period.end });
    setIsRunPayrollModalOpen(true);
  };

  const handleOpenRunPayrollModal = () => {
    setRunPayrollInitialPeriod(null);
    setIsRunPayrollModalOpen(true);
  };

  const handleCloseRunPayrollModal = () => {
    setIsRunPayrollModalOpen(false);
    setRunPayrollInitialPeriod(null);
  };

  // Edit handlers
  const handleEditEntry = (entry: PayrollEntry) => {
    setEditingEntry(entry);
    setCreatingResourceId(null);
    setIsEditModalOpen(true);
  };

  // Create handler
  const handleCreateEntry = (resourceId: string, kind: 'timeclock' | 'bonus' = 'timeclock', name = '') => {
    setCreateKind(kind);
    setCreatingResourceId(resourceId);
    setCreatingResourceName(name);
    setEditingEntry(null);
    setIsEditModalOpen(true);
  };

  const handleOpenAddEntry = async () => {
    setPickerPurpose('timeclock');
    await openResourcePicker();
  };

  const handleOpenAddBonus = async () => {
    setPickerPurpose('bonus');
    await openResourcePicker();
  };

  const openResourcePicker = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    setIsResourcePickerOpen(true);
    setPickerLoading(true);
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('id, first_name, last_name, avatar_url, compensation_type, is_active')
        .eq('business_id', selectedBusiness.id)
        .eq('is_active', true)
        .order('first_name');

      if (error) throw error;
      setPickerResources(data || []);
    } catch (error) {
      console.error('Error loading resources for entry picker:', error);
      toast.error('Failed to load employees');
      setIsResourcePickerOpen(false);
    } finally {
      setPickerLoading(false);
    }
  };

  const handlePickResource = (resourceId: string, name: string) => {
    setIsResourcePickerOpen(false);
    handleCreateEntry(resourceId, pickerPurpose, name);
  };

  const handleCreateNewEntry = async (entryData: any) => {
    if (!selectedBusiness?.id || !creatingResourceId) return;

    try {
      if (entryData?.kind === 'bonus') {
        await createCustomBonus(selectedBusiness.id, creatingResourceId, entryData);
        toast.success('Bonus added');
      } else {
        await createPayrollEntry(selectedBusiness.id, creatingResourceId, entryData);
        toast.success('Timeclock entry created successfully');
      }
      loadEntries();
      setIsEditModalOpen(false);
      setCreatingResourceId(null);
      setCreatingResourceName('');
    } catch (error) {
      console.error('Error creating payroll entry:', error);
      toast.error(entryData?.kind === 'bonus' ? 'Failed to add bonus' : 'Failed to create timeclock entry');
      throw error;
    }
  };

  const handleSaveEdit = async (entryId: string, updates: any) => {
    try {
      await updatePayrollEntry(entryId, updates);
      toast.success('Payroll entry updated successfully');
      loadEntries();
    } catch (error) {
      console.error('Error updating payroll entry:', error);
      toast.error('Failed to update payroll entry');
    }
  };

  const handleTimeclockUpdated = async (result: UpdateEntryResult) => {
    if (result.adjustment) {
      const delta = result.adjustment.delta;
      toast.success(`Shift updated. ${delta >= 0 ? '+' : '-'}$${Math.abs(delta).toFixed(2)} adjustment added to the next payroll run.`);
    } else {
      toast.success('Shift updated');
    }
    loadEntries();
  };

  const handleConfirmEntry = async (entryId: string) => {
    try {
      await confirmTimeclockEntry(entryId);
      toast.success('Shift confirmed');
      loadEntries();
    } catch (error) {
      console.error('Error confirming shift:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to confirm shift');
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    try {
      await deletePayrollEntry(entryId);
      toast.success('Payroll entry deleted successfully');
      loadEntries();
    } catch (error) {
      console.error('Error deleting payroll entry:', error);
      toast.error('Failed to delete payroll entry');
    }
  };


  const toggleResource = (resourceId: string) => {
    setExpandedResources(prev => {
      const next = new Set(prev);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  };

  const toggleBooking = (bookingKey: string) => {
    setExpandedBookings(prev => {
      const next = new Set(prev);
      if (next.has(bookingKey)) {
        next.delete(bookingKey);
      } else {
        next.add(bookingKey);
      }
      return next;
    });
  };

  // Group entries by resource and booking
  const entriesByResource = entries.reduce<Record<string, {
    resource: {
      first_name: string;
      last_name: string;
      email: string;
      compensation_type: string;
      flat_rate_amount: number | null;
      hourly_rate: number | null;
      avatar_url?: string;
    };
    timeclock: {
      entries: PayrollEntry[];
      totalHours: number;
      totalEarnings: number;
    };
    bookings: Record<string, {
      booking_number: number;
      booking_title: string;
      start_date: string;
      end_date: string;
      items: PayrollEntry[];
      totalHours: number;
      totalEarnings: number;
    }>;
    bonuses: {
      entries: PayrollEntry[];
      totalEarnings: number;
    };
    tips: {
      entries: PayrollEntry[];
      totalEarnings: number;
    };
    adjustments: {
      entries: PayrollEntry[];
      totalEarnings: number;
    };
    needsReviewCount: number;
    totalHours: number;
    totalEarnings: number;
  }>>((acc, entry) => {
    if (!acc[entry.resource_id]) {
      acc[entry.resource_id] = {
        resource: {
          first_name: entry.resource_first_name,
          last_name: entry.resource_last_name,
          email: entry.resource_email,
          compensation_type: entry.compensation_type,
          flat_rate_amount: entry.flat_rate_amount,
          hourly_rate: entry.hourly_rate,
          avatar_url: entry.resource_avatar_url,
        },
        timeclock: {
          entries: [],
          totalHours: 0,
          totalEarnings: 0
        },
        bookings: {},
        bonuses: {
          entries: [],
          totalEarnings: 0
        },
        tips: {
          entries: [],
          totalEarnings: 0
        },
        adjustments: {
          entries: [],
          totalEarnings: 0
        },
        needsReviewCount: 0,
        totalHours: 0,
        totalEarnings: 0
      };
    }

    if (entry.entry_type === 'timeclock') {
      acc[entry.resource_id].timeclock.entries.push(entry);
      acc[entry.resource_id].timeclock.totalHours += entry.hours || 0;
      acc[entry.resource_id].timeclock.totalEarnings += entry.earnings || 0;
      if (entry.needs_review) acc[entry.resource_id].needsReviewCount += 1;
    } else if (entry.entry_type === 'adjustment') {
      acc[entry.resource_id].adjustments.entries.push(entry);
      acc[entry.resource_id].adjustments.totalEarnings += entry.earnings || 0;
    } else if (entry.entry_type === 'bonus') {
      acc[entry.resource_id].bonuses.entries.push(entry);
      acc[entry.resource_id].bonuses.totalEarnings += entry.earnings || 0;
    } else if (entry.entry_type === 'tip') {
      acc[entry.resource_id].tips.entries.push(entry);
      acc[entry.resource_id].tips.totalEarnings += entry.earnings || 0;
    } else {
      const bookingKey = entry.booking_id || 'no-booking';
      if (!acc[entry.resource_id].bookings[bookingKey]) {
        acc[entry.resource_id].bookings[bookingKey] = {
          booking_number: entry.booking_number || 0,
          booking_title: entry.booking_title || '',
          start_date: entry.start_date || entry.created_at,
          end_date: entry.end_date || entry.created_at,
          items: [],
          totalHours: 0,
          totalEarnings: 0
        };
      }
      acc[entry.resource_id].bookings[bookingKey].items.push(entry);
      acc[entry.resource_id].bookings[bookingKey].totalHours += entry.hours || 0;
      acc[entry.resource_id].bookings[bookingKey].totalEarnings += entry.earnings || 0;
    }

    acc[entry.resource_id].totalHours += entry.hours || 0;
    acc[entry.resource_id].totalEarnings += entry.earnings || 0;
    return acc;
  }, {});

  // Money shown on the page comes from the database's own sums when available;
  // the client grouping above only organises entries for display.
  for (const [resourceId, resourceData] of Object.entries(entriesByResource)) {
    const t = dbTotals[resourceId];
    if (!t) continue;
    resourceData.timeclock.totalHours = t.timeclock_minutes / 60;
    resourceData.timeclock.totalEarnings = t.timeclock_earnings;
    resourceData.bonuses.totalEarnings = t.bonus_earnings;
    resourceData.tips.totalEarnings = t.tip_earnings;
    resourceData.adjustments.totalEarnings = t.adjustment_earnings;
    resourceData.totalHours = t.timeclock_minutes / 60 + t.flat_rate_hours;
    resourceData.totalEarnings = t.total_earnings;
    resourceData.needsReviewCount = t.needs_review_count;
  }

  // Get all resource entries
  const filteredEntriesByResource = Object.entries(entriesByResource);

  const payrollTotals = useMemo(() => {
    let totalEarnings = 0;
    let totalHours = 0;
    for (const [, resourceData] of filteredEntriesByResource) {
      totalEarnings += resourceData.totalEarnings;
      totalHours += resourceData.totalHours;
    }
    const needsReview = filteredEntriesByResource.reduce((sum, [, r]) => sum + r.needsReviewCount, 0);
    return {
      totalEarnings,
      totalHours,
      resourceCount: filteredEntriesByResource.length,
      needsReview,
    };
  }, [filteredEntriesByResource]);

  const isPayrollDataLoading = !settingsReady || entriesLoading;

  if (error && !isPayrollDataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}>
        <div className="text-center max-w-sm">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-4">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <X className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-[20px] font-semibold text-gray-900 mb-1.5">Error Loading Data</h3>
            <p className="text-[15px] text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadEntries}
              className="w-full py-3.5 px-4 bg-blue-600 text-white rounded-xl text-[17px] font-semibold active:bg-blue-700 transition-colors touch-manipulation"
              style={{ minHeight: '44px' }}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gray-50 pb-safe" 
      data-manage-payroll-page
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}
    >
      {isDateRangeOpen && createPortal(
                <div 
                  className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center"
                  onClick={() => setIsDateRangeOpen(false)}
                  style={{ 
                    paddingTop: 'env(safe-area-inset-top)',
                    paddingBottom: 'env(safe-area-inset-bottom)',
                    paddingLeft: 'env(safe-area-inset-left)',
                    paddingRight: 'env(safe-area-inset-right)'
                  }}
                >
                  <div 
                    className="bg-white rounded-2xl shadow-2xl overflow-hidden mx-4"
                    style={{
                      minWidth: '280px',
                      maxWidth: '400px',
                      width: '100%'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {DATE_RANGES.map((range, index) => {
                      const rangeData = range.getRange();
                      const showDates = range.value === 'current_period' || range.value === 'last_period';
                      const isSelected = selectedRange.value === range.value;
                      const isCustom = range.value === 'custom';
                      
                      return (
                        <button
                          key={range.value}
                          onClick={() => {
                            setSelectedRange(range);
                            if (isCustom) {
                              if (!customStartDate) {
                                setCustomStartDate(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
                              }
                              if (!customEndDate) {
                                setCustomEndDate(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
                              }
                              return;
                            }
                            setIsDateRangeOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-4 py-3.5 text-[17px] transition-colors touch-manipulation ${
                            index !== DATE_RANGES.length - 1 || isCustom ? 'border-b border-gray-100' : ''
                          } ${isSelected ? 'bg-blue-50' : 'active:bg-gray-50'}`}
                          style={{ minHeight: '44px' }}
                        >
                          <div className="flex items-center gap-3">
                            <Calendar className={`h-5 w-5 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                            <span className={`font-medium ${isSelected ? 'text-blue-600' : 'text-gray-900'}`}>
                              {range.label}
                            </span>
                          </div>
                          {showDates && (
                            <span className="text-[15px] text-gray-500">
                              {format(rangeData.start, 'MMM d')} - {format(rangeData.end, 'MMM d')}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {selectedRange.value === 'custom' ? (
                      <div className="px-4 py-4 space-y-3 border-t border-gray-100 bg-gray-50/80">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Start date
                          </label>
                          <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            End date
                          </label>
                          <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <button
                          type="button"
                          disabled={!isCustomRangeValid}
                          onClick={() => setIsDateRangeOpen(false)}
                          className={`w-full py-3 rounded-lg text-[15px] font-semibold transition-colors touch-manipulation ${
                            isCustomRangeValid
                              ? 'bg-blue-600 text-white active:bg-blue-700'
                              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          Apply
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>,
                document.body
      )}

      <div className="px-4 sm:px-6 lg:px-8 pt-3 pb-6 sm:pb-8">
        {pastDueHasLoaded && pastDuePeriods.length > 0 ? (
          <PayrollPastDuePanel
            periods={pastDuePeriods}
            onSelectPeriod={handleSelectPastDuePeriod}
          />
        ) : null}

        {!isPayrollDataLoading && payrollTotals.needsReview > 0 && (
          <div className="mb-3 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-[13px] text-amber-900">
              <span className="font-semibold">{payrollTotals.needsReview} shift{payrollTotals.needsReview === 1 ? '' : 's'} need review</span>
              {' '}— unusually long shifts are held out of Process Payroll until you confirm or correct them. Open the shift to confirm.
            </div>
          </div>
        )}

        <PayrollEntriesPanel
          selectedRangeLabel={dateRangeLabel}
          dateRange={dateRange}
          payrollSettings={payrollSettings}
          totalEarnings={payrollTotals.totalEarnings}
          totalHours={payrollTotals.totalHours}
          resourceCount={payrollTotals.resourceCount}
          isLoading={isPayrollDataLoading}
          onAddEntry={handleOpenAddEntry}
          onAddBonus={handleOpenAddBonus}
          onOpenDateRange={() => setIsDateRangeOpen((open) => !open)}
          isDateRangeOpen={isDateRangeOpen}
          onOpenHistory={() => setIsHistoryModalOpen(true)}
          onOpenRequests={handleOpenPayrollRequestsModal}
          onOpenRunPayroll={handleOpenRunPayrollModal}
          pendingRequestsCount={pendingRequestsCount}
        >
          {isPayrollDataLoading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3">
              {[0, 1].map((key) => (
                <div
                  key={key}
                  className="h-[120px] sm:h-[132px] rounded-2xl border border-gray-200/50 bg-gray-50 animate-pulse"
                />
              ))}
            </div>
          ) : filteredEntriesByResource.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4">
              <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-gray-100 flex items-center justify-center mb-3 sm:mb-4">
                <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
              </div>
              <h3 className="text-[18px] sm:text-[20px] font-semibold text-gray-900 mb-1 sm:mb-1.5">
                No Payroll Entries
              </h3>
              <p className="text-[13px] sm:text-[15px] text-gray-500 text-center max-w-[280px]">
                No payroll entries found for the selected date range.
              </p>
              <button
                onClick={handleOpenAddEntry}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-[14px] font-semibold hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] transition-all touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent', minHeight: '40px' }}
              >
                <Plus className="h-4 w-4" />
                Add Entry
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3">
              {filteredEntriesByResource.map(([resourceId, resourceData]) => (
                <PayrollResourceSummary
                  key={resourceId}
                  resourceId={resourceId}
                  data={resourceData}
                  isExpanded={expandedResources.has(resourceId)}
                  onToggle={() => toggleResource(resourceId)}
                  onToggleBooking={toggleBooking}
                  expandedBookings={expandedBookings}
                  onEditEntry={handleEditEntry}
                  onCreateEntry={(resourceId) => handleCreateEntry(resourceId, 'timeclock', `${resourceData.resource.first_name} ${resourceData.resource.last_name}`.trim())}
                  onCreateBonus={(resourceId) => handleCreateEntry(resourceId, 'bonus', `${resourceData.resource.first_name} ${resourceData.resource.last_name}`.trim())}
                  onConfirmEntry={handleConfirmEntry}
                />
              ))}
            </div>
          )}
        </PayrollEntriesPanel>
      </div>

      {/* Time Requests Modal - Right Side Mounted */}
      {(isPayrollRequestsModalOpen || isPayrollRequestsAnimating) && createPortal(
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 z-[99998] ${
              isPayrollRequestsModalOpen ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={handleClosePayrollRequestsModal}
          />
          {/* Panel */}
          <div
            className={`fixed inset-y-0 right-0 z-[99999] w-full sm:w-[480px] md:w-[600px] max-w-full bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${
              isPayrollRequestsModalOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onTransitionEnd={handlePayrollRequestsTransitionEnd}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with safe area for notch */}
            <div 
              className="bg-white border-b border-gray-100 flex items-center justify-between shrink-0 px-5"
              style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: '0.75rem' }}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-blue-50">
                  <Inbox className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-[17px] font-semibold text-gray-900 leading-tight">Time Requests</h2>
                  {pendingRequestsCount > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">{pendingRequestsCount} pending</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Filter Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsPayrollFilterOpen(!isPayrollFilterOpen)}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
                  >
                    <div className={`w-2 h-2 rounded-full ${
                      activeFilter === 'pending' ? 'bg-orange-500' : activeFilter === 'approved' ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                    <span className="capitalize">{activeFilter}</span>
                    <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${isPayrollFilterOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isPayrollFilterOpen && (
                    <div className="absolute top-full right-0 mt-1.5 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden py-1">
                      <button
                        onClick={() => { setActiveFilter('pending'); setIsPayrollFilterOpen(false); }}
                        className="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span>Pending</span>
                      </button>
                      <button
                        onClick={() => { setActiveFilter('approved'); setIsPayrollFilterOpen(false); }}
                        className="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Approved</span>
                      </button>
                      <button 
                        onClick={() => { setActiveFilter('denied'); setIsPayrollFilterOpen(false); }}
                        className="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span>Denied</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Close button */}
                <button
                  onClick={handleClosePayrollRequestsModal}
                  className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors active:bg-gray-200 active:scale-95 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          
            {/* Modal Content */}
            <div className="flex-1 overflow-hidden">
              <PayrollRequestsSection 
                businessId={selectedBusiness?.id || ''}
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                onRequestProcessed={() => {
                  loadEntries();
                  loadPendingRequestsCount();
                }}
              />
            </div>
          </div>
        </>,
        document.body
      )}

      {/* RunPayrollModal */}
      <RunPayrollModal
        isOpen={isRunPayrollModalOpen}
        onClose={handleCloseRunPayrollModal}
        businessId={selectedBusiness?.id || ''}
        payrollSettings={payrollSettings}
        initialPeriod={runPayrollInitialPeriod}
        onPayrollComplete={() => {
          loadEntries();
          setPastDueRefreshKey((key) => key + 1);
        }}
        onOpenHistory={() => setIsHistoryModalOpen(true)}
      />

      {/* PayrollHistoryModal */}
      <PayrollHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        businessId={selectedBusiness?.id || ''}
      />

      {/* PayrollEditModal */}
      <PayrollEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingEntry(null);
          setCreatingResourceId(null);
          setCreatingResourceName('');
        }}
        entry={editingEntry}
        onSave={handleSaveEdit}
        onTimeclockUpdated={handleTimeclockUpdated}
        onDelete={handleDeleteEntry}
        resourceId={creatingResourceId || undefined}
        resourceName={creatingResourceName}
        createKind={createKind}
        businessId={selectedBusiness?.id || undefined}
        onCreate={handleCreateNewEntry}
      />

      {/* Employee picker for page-level Add Entry */}
      {isResourcePickerOpen && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center"
          style={{ zIndex: 100300, backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
          onClick={() => setIsResourcePickerOpen(false)}
        >
          <div
            className="bg-white w-full sm:w-[400px] sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-[17px] font-semibold text-gray-900">{pickerPurpose === 'bonus' ? 'Add bonus' : 'Add Payroll Entry'}</h3>
                <p className="text-[13px] text-gray-500 mt-0.5">Choose an employee</p>
              </div>
              <button
                onClick={() => setIsResourcePickerOpen(false)}
                className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3" style={{ WebkitOverflowScrolling: 'touch' }}>
              {pickerLoading ? (
                <div className="space-y-2">
                  {[0, 1, 2].map((key) => (
                    <div key={key} className="h-14 rounded-xl bg-gray-100 animate-pulse" />
                  ))}
                </div>
              ) : pickerResources.length === 0 ? (
                <p className="text-center text-[14px] text-gray-500 py-8">
                  No active employees found. Add employees first, then create payroll entries for them.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {pickerResources.map((resource) => (
                    <button
                      key={resource.id}
                      onClick={() => handlePickResource(resource.id, `${resource.first_name} ${resource.last_name}`.trim())}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors text-left touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent', minHeight: '56px' }}
                    >
                      {resource.avatar_url ? (
                        <img
                          src={resource.avatar_url}
                          alt=""
                          className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                          {resource.first_name?.[0]}{resource.last_name?.[0]}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-[15px] font-medium text-gray-900 truncate">
                          {resource.first_name} {resource.last_name}
                        </div>
                        {resource.compensation_type && (
                          <div className="text-[12px] text-gray-500 capitalize">
                            {resource.compensation_type.replace('_', ' ')}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
